import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Package, Plus, Save } from 'lucide-react';

import { deliveriesAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from '../ui/use-toast';

// 2026-08-24: Docs/COMMUNITY_SERVICE_DELIVERY_TRACKING_PLAN.md §5 — where
// an admin defines what "delivery" means for their account. Mirrors
// CallingVoiceSettings.tsx's shape (load-on-mount, edit local state, POST
// on save) — smaller in scope per row, but a list + add-dialog rather than
// one form, since an account configures several delivery types over time.
// Backend: app/api/deliveries.py, app/services/delivery_service.py.

type DeliveryType = {
  id: number;
  archetype: string;
  label: string;
  requires_photo: boolean;
  requires_geolocation: boolean;
  requires_verification: boolean;
  escalation_hours: number | null;
  active: boolean;
};

type DeliveryItem = {
  id: number;
  delivery_type_config_id: number;
  name: string;
  unit: string | null;
  unit_value: number | null;
  currency: string | null;
  active: boolean;
};

// Archetype defaults shown in the picker — mirrors
// DeliveryArchetype.default_toggles on the backend (service_delivery.py)
// so the dialog's pre-fill matches what create_delivery_type actually
// applies when a field is left unset.
const ARCHETYPES: {
  value: string;
  label: string;
  glyph: string;
  defaults: string;
  requires_photo: boolean;
  requires_verification: boolean;
}[] = [
  { value: 'physical_item', label: 'Physical Item', glyph: '🧺', defaults: 'photo · verify', requires_photo: true, requires_verification: true },
  { value: 'session_visit', label: 'Session / Visit', glyph: '🗣️', defaults: 'form only', requires_photo: false, requires_verification: false },
  { value: 'voucher', label: 'Voucher', glyph: '🎫', defaults: 'photo', requires_photo: true, requires_verification: false },
  { value: 'screening_sample', label: 'Screening', glyph: '🩺', defaults: 'form · verify', requires_photo: false, requires_verification: true },
  { value: 'referral_case', label: 'Referral / Case', glyph: '⚖️', defaults: 'stays a ticket', requires_photo: false, requires_verification: true },
];

const ARCHETYPE_META = Object.fromEntries(ARCHETYPES.map((a) => [a.value, a]));

export default function ServiceDeliverySettings({ readOnly = false }: { readOnly?: boolean }) {
  const [types, setTypes] = useState<DeliveryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [archetype, setArchetype] = useState('physical_item');
  const [label, setLabel] = useState('');
  const [requiresPhoto, setRequiresPhoto] = useState(true);
  const [requiresGeo, setRequiresGeo] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(true);
  const [escalationHours, setEscalationHours] = useState('');
  const [saving, setSaving] = useState(false);

  const [itemsOpenFor, setItemsOpenFor] = useState<DeliveryType | null>(null);
  const [items, setItems] = useState<DeliveryItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemUnit, setNewItemUnit] = useState('');
  const [newItemValue, setNewItemValue] = useState('');
  const [newItemCurrency, setNewItemCurrency] = useState('XOF');
  const [addingItem, setAddingItem] = useState(false);

  const load = () => {
    setLoading(true);
    deliveriesAPI
      .listDeliveryTypes(false)
      .then((data) => setTypes(data || []))
      .catch((err: any) => setError(err?.response?.data?.detail || err?.message || 'Failed to load package types'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const selectedArchetype = useMemo(() => ARCHETYPE_META[archetype], [archetype]);

  const applyArchetypeDefaults = (value: string) => {
    setArchetype(value);
    const meta = ARCHETYPE_META[value];
    setRequiresPhoto(meta.requires_photo);
    setRequiresVerification(meta.requires_verification);
  };

  const resetAddForm = () => {
    setArchetype('physical_item');
    setLabel('');
    setRequiresPhoto(true);
    setRequiresGeo(false);
    setRequiresVerification(true);
    setEscalationHours('');
  };

  const handleCreate = async () => {
    if (!label.trim()) {
      toast({ title: 'Label required', description: 'Give this package type a name.', variant: 'destructive' as any });
      return;
    }
    setSaving(true);
    try {
      const created = await deliveriesAPI.createDeliveryType({
        archetype,
        label: label.trim(),
        requires_photo: requiresPhoto,
        requires_geolocation: requiresGeo,
        requires_verification: requiresVerification,
        escalation_hours: escalationHours ? Number(escalationHours) : undefined,
      });
      setTypes((prev) => [...prev, created]);
      toast({ title: 'Package type added', description: `"${created.label}" is ready to send.` });
      setAddOpen(false);
      resetAddForm();
    } catch (err: any) {
      toast({
        title: 'Couldn’t add package type',
        description: err?.response?.data?.detail || err?.message || 'Something went wrong.',
        variant: 'destructive' as any,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (type: DeliveryType) => {
    try {
      const updated = await deliveriesAPI.updateDeliveryType(type.id, { active: !type.active });
      setTypes((prev) => prev.map((t) => (t.id === type.id ? updated : t)));
    } catch (err: any) {
      toast({
        title: 'Couldn’t update',
        description: err?.response?.data?.detail || err?.message,
        variant: 'destructive' as any,
      });
    }
  };

  const openItems = (type: DeliveryType) => {
    setItemsOpenFor(type);
    setItems([]);
    setItemsLoading(true);
    deliveriesAPI
      .listItems(type.id, false)
      .then((data) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  };

  const addItem = async () => {
    if (!itemsOpenFor || !newItemName.trim()) return;
    if (newItemValue && !newItemCurrency) {
      toast({ title: 'Currency required', description: 'A value needs a currency code (e.g. XOF, USD).', variant: 'destructive' as any });
      return;
    }
    setAddingItem(true);
    try {
      const created = await deliveriesAPI.createItem(itemsOpenFor.id, {
        name: newItemName.trim(),
        unit: newItemUnit.trim() || undefined,
        unit_value: newItemValue ? Number(newItemValue) : undefined,
        currency: newItemValue ? newItemCurrency.trim().toUpperCase() : undefined,
      });
      setItems((prev) => [...prev, created]);
      setNewItemName('');
      setNewItemUnit('');
      setNewItemValue('');
    } catch (err: any) {
      toast({
        title: 'Couldn’t add item',
        description: err?.response?.data?.detail || err?.message,
        variant: 'destructive' as any,
      });
    } finally {
      setAddingItem(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading package types…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-5 w-5" />
              Package Types
            </CardTitle>
            <CardDescription className="mt-1">
              The service packages your team can send to beneficiaries — kits, counselling sessions,
              vouchers, screenings, or a case that stays a plain ticket. Each type is built from an
              archetype that sets sensible proof/verification defaults, adjustable per type.
            </CardDescription>
          </div>
          {!readOnly && (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add package type
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {types.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No package types configured yet — add one to start sending kit, voucher, or counselling
              session packages for this account.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Archetype</TableHead>
                  <TableHead>Photo</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Verify</TableHead>
                  <TableHead>Escalate</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((t) => {
                  const meta = ARCHETYPE_META[t.archetype];
                  return (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">
                        <span className="mr-2">{meta?.glyph || '📦'}</span>
                        {t.label}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono text-[11px]">
                          {t.archetype}
                        </Badge>
                      </TableCell>
                      <TableCell>{t.requires_photo ? '✓' : '–'}</TableCell>
                      <TableCell>{t.requires_geolocation ? '✓' : '–'}</TableCell>
                      <TableCell>{t.requires_verification ? '✓' : '–'}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {t.escalation_hours ? `${t.escalation_hours}h` : '—'}
                      </TableCell>
                      <TableCell>
                        <Switch checked={t.active} disabled={readOnly} onCheckedChange={() => toggleActive(t)} />
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => openItems(t)}>
                          Items
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add package type */}
      <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) resetAddForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add a package type</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div>
              <Label className="mb-2 block text-xs uppercase tracking-wide text-muted-foreground">
                Start from an archetype
              </Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {ARCHETYPES.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => applyArchetypeDefaults(a.value)}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      archetype === a.value
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border/60 hover:bg-muted/40'
                    }`}
                  >
                    <div className="text-base">{a.glyph}</div>
                    <div className="mt-1 text-xs font-semibold">{a.label}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{a.defaults}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="dt-label">Label</Label>
                <Input
                  id="dt-label"
                  placeholder={selectedArchetype ? `e.g. "${selectedArchetype.label}"` : 'e.g. Nutrition Kit'}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dt-escalation">Notify supervisor if not delivered within</Label>
                <Input
                  id="dt-escalation"
                  type="number"
                  placeholder="hours (optional)"
                  value={escalationHours}
                  onChange={(e) => setEscalationHours(e.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex items-center gap-2">
                  <Switch id="dt-photo" checked={requiresPhoto} onCheckedChange={setRequiresPhoto} />
                  <Label htmlFor="dt-photo" className="text-sm">Requires photo</Label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="dt-geo" checked={requiresGeo} onCheckedChange={setRequiresGeo} />
                <Label htmlFor="dt-geo" className="text-sm">Requires location</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="dt-verify" checked={requiresVerification} onCheckedChange={setRequiresVerification} />
                <Label htmlFor="dt-verify" className="text-sm">Requires supervisor verification</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Add package type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item catalog for one package type */}
      <Dialog open={!!itemsOpenFor} onOpenChange={(open) => !open && setItemsOpenFor(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{itemsOpenFor?.label} — items</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {itemsLoading ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items yet — add one below.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.name}</TableCell>
                      <TableCell className="text-muted-foreground">{it.unit || '—'}</TableCell>
                      <TableCell className="font-mono">
                        {it.unit_value != null ? `${it.unit_value.toLocaleString()} ${it.currency}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {!readOnly && (
              <>
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-border/60 bg-muted/20 p-3 sm:grid-cols-5">
                  <Input placeholder="Item name" className="sm:col-span-2" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} />
                  <Input placeholder="Unit (e.g. kit)" value={newItemUnit} onChange={(e) => setNewItemUnit(e.target.value)} />
                  <Input placeholder="Value" type="number" value={newItemValue} onChange={(e) => setNewItemValue(e.target.value)} />
                  <div className="flex gap-1">
                    <Input placeholder="XOF" value={newItemCurrency} onChange={(e) => setNewItemCurrency(e.target.value)} className="w-16" />
                    <Button size="icon" variant="outline" onClick={addItem} disabled={addingItem || !newItemName.trim()}>
                      {addingItem ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Value is optional — set it for donor/financial reporting. What a specific delivery was
                  actually worth is recorded when a worker marks it delivered, using the value set here at
                  that moment (editing it later doesn't change past deliveries).
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
